import {
  CUSTOM_BLOTTER_GROUPING,
  MixpanelEvent,
  columnTypes,
  type BlotterGroupingDefinition,
  type Position,
} from '@talos/kyoko';
import type { GridOptions } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../../hooks';
import { GROUP_COL_WIDTH } from '../tokens';
import { SubAccountPositionsBlotterMode, type ExtraPositionColumns } from '../types';

type SubAccGroupings = 'Sub Account' | 'Underlying' | 'Product Type' | typeof CUSTOM_BLOTTER_GROUPING;
type GroupablePositionColumns = keyof Position | ExtraPositionColumns;

interface UseRowGroupVariantSpecsOutput {
  groupings: BlotterGroupingDefinition<SubAccGroupings, GroupablePositionColumns>[];
  groupingsRowGroupColumns: string[][];
  gridOptionsOverlay: Pick<
    GridOptions,
    'groupDisplayType' | 'groupRemoveLowestSingleChildren' | 'autoGroupColumnDef' | 'rowGroupPanelShow'
  >;
}

/**
 * This hook encapsulates the RowGroup-variant specific specifications for the Sub Account Positions blotter.
 * Its really just a way of tucking it away in one place for organisational purposes.
 */
export const useRowGroupVariantSpecs = (): UseRowGroupVariantSpecsOutput => {
  const { subAccountPositionsBlotterMode } = useFeatureFlag();

  const advancedMode =
    subAccountPositionsBlotterMode === SubAccountPositionsBlotterMode.Advanced ||
    subAccountPositionsBlotterMode === 'DerivativesAdvanced'; // old defaulting code, remove for 2.50

  const groupings: BlotterGroupingDefinition<SubAccGroupings, GroupablePositionColumns>[] = useMemo(() => {
    return compact([
      {
        key: 'Sub Account',
        label: 'By Sub Account',
        rowGroupColumns: ['SubAccount'],
        mixpanelEvent: MixpanelEvent.GroupAccounts,
        buttonDataTestId: 'by-sub-account-grouping-button',
      },
      {
        key: 'Underlying',
        label: 'By Underlying',
        rowGroupColumns: ['underlying', 'SubAccount'],
        mixpanelEvent: MixpanelEvent.GroupUnderlying,
        buttonDataTestId: 'by-underlying-grouping-button',
      },
      {
        key: 'Product Type',
        label: 'By Product Type',
        rowGroupColumns: ['AssetType', 'SubAccount'],
        mixpanelEvent: MixpanelEvent.GroupProductType,
        buttonDataTestId: 'by-product-type-grouping-button',
      },
      advancedMode && {
        key: CUSTOM_BLOTTER_GROUPING,
        label: 'Custom',
        rowGroupColumns: [],
        buttonDataTestId: 'custom-grouping-button',
      },
    ]);
  }, [advancedMode]);
  const groupingsRowGroupColumns: string[][] = useMemo(() => groupings.map(g => g.rowGroupColumns), [groupings]);

  const output: UseRowGroupVariantSpecsOutput = useMemo(
    () => ({
      groupings,
      groupingsRowGroupColumns,
      gridOptionsOverlay: {
        groupDisplayType: 'singleColumn',
        groupRemoveLowestSingleChildren: true,
        autoGroupColumnDef: columnTypes.group({
          type: 'group',
          title: 'Grouping',
          editable: false,
          initialWidth: GROUP_COL_WIDTH,
          initialHide: false,
          initialSort: '+',
          suppressColumnsToolPanel: false,
          params: {
            suppressCount: true,
          },
        }),
        rowGroupPanelShow: advancedMode ? 'always' : 'never',
      },
    }),
    [advancedMode, groupings, groupingsRowGroupColumns]
  );

  return output;
};
